import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";

import { ApiExplorerWorkspace } from "@/components/api-explorer/ApiExplorerWorkspace";
import { getServiceSpec } from "@/lib/api-explorer/load-specs";
import { loadOperations } from "@/lib/api-explorer/spec-loader";

export const Route = createFileRoute("/_authenticated/api-explorer/$service")({
  // `getServiceSpec` is async now — the catalog lives in northstar-auth
  // and is fetched once per SPA session via `loadCatalog()`. TanStack
  // Router awaits the loader before rendering the component, so the
  // child code below still gets `meta` synchronously.
  loader: async ({ params }) => {
    const meta = await getServiceSpec(params.service);
    if (!meta) throw notFound();
    return { meta: meta.meta };
  },
  component: ServiceWorkspaceRoute,
});

function ServiceWorkspaceRoute() {
  const { meta } = Route.useLoaderData();
  const { service } = Route.useParams();

  const opsQuery = useQuery({
    queryKey: ["api-explorer", "operations", service],
    queryFn: () => loadOperations(service),
    staleTime: Infinity,
  });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <Link
          to="/api-explorer"
          className="text-muted-foreground hover:text-foreground flex items-center gap-1 self-start text-xs"
        >
          <ArrowLeft className="size-3" /> All services
        </Link>
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">
            {meta.title}
          </h2>
          <p className="text-muted-foreground font-mono text-xs">
            /auth/proxy/{meta.id}
          </p>
        </div>
      </div>

      {opsQuery.isLoading ? (
        <p className="text-muted-foreground text-sm">Parsing spec…</p>
      ) : opsQuery.isError ? (
        <p className="text-destructive text-sm">
          Failed to parse spec: {(opsQuery.error as Error).message}
        </p>
      ) : (
        <ApiExplorerWorkspace service={meta} operations={opsQuery.data ?? []} />
      )}
    </div>
  );
}
